import { Routes, Route, useLocation, Navigate } from "react-router-dom";

// Old invitation links used /signup — forward them to /onboarding with the same details.
const SignupRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/onboarding${search}`} replace />;
};
import { useEffect } from "react";
import { OrgFeatureGate } from "@/components/auth/OrgFeatureGate";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import { BrandGate } from "@/components/brand/BrandGate";
import WhiteLabelStudio from "./pages/WhiteLabelStudio";
import SubCustomers from "./pages/SubCustomers";
import SubCustomerDetail from "./pages/SubCustomerDetail";
import Login from "./pages/Login";
import AlertUnsubscribed from "./pages/AlertUnsubscribed";
import Dashboard from "./pages/Dashboard";
import DeviceDetails from "./pages/DeviceDetails";
import DeviceAssignments from "./pages/DeviceAssignments";
import DeviceGroups from "./pages/DeviceGroups";
import Gallery from "./pages/Gallery";
import Profile from "./pages/Profile";
import UserRoles from "./pages/UserRoles";
import BulkUpdate from "./pages/BulkUpdate";
import CustomerSuccess from "./pages/CustomerSuccess";
import { SuperuserGate } from "./components/auth/SuperuserGate";
import { NewSiteHome, NewSiteAbout, NewSiteNews, NewSiteSolution, NewSiteCaseStudies, NewSiteCaseStudyPage, NewSiteNewsArticlePage, NewSiteSectors } from "./pages/new-site/NewSitePages";
import Notifications from "./pages/Notifications";
import Admin from "./pages/Admin";
import AdminOrders from "./pages/AdminOrders";
import OrganisationProfile from "./pages/OrganisationProfile";
import ApiConfiguration from "./pages/ApiConfiguration";
import Documentation from "./pages/Documentation";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import Applications from "./pages/Applications";
import Equipment from "./pages/Equipment";
import Map from "./pages/Map";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Disclaimer from "./pages/Disclaimer";
import DataProtection from "./pages/DataProtection";
import Cookies from "./pages/Cookies";
import Environmental from "./pages/Environmental";
import ModernSlavery from "./pages/ModernSlavery";
import BriberyPolicy from "./pages/BriberyPolicy";
import Invite from "./pages/Invite";
import InviteContactLink from "./pages/InviteContactLink";
import OnboardingSignup from "./pages/OnboardingSignup";
import { CustomerProposal } from "./pages/CustomerProposal";
import ProposalAcceptance from "./pages/ProposalAcceptance";
import CrmContacts from "./pages/crm/CrmContacts";
import CrmStaff from "./pages/crm/CrmStaff";
import CrmPartners from "./pages/crm/CrmPartners";
import CrmCompanies from "./pages/crm/CrmCompanies";
import CrmDeals from "./pages/crm/CrmDeals";
import CrmOrders from "./pages/crm/CrmOrders";
import CrmActivities from "./pages/crm/CrmActivities";
import CrmKpi from "./pages/crm/CrmKpi";
import CrmPipeline from "./pages/crm/CrmPipeline";
import MarketingCalendar from "./pages/crm/MarketingCalendar";

import PartnerPortal from "./pages/PartnerPortal";
import PartnerOrders from "./pages/PartnerOrders";
import PartnerResources from "./pages/PartnerResources";
import PartnerInviteAccept from "./pages/PartnerInviteAccept";
import ProposalSignup from "./pages/ProposalSignup";
import ProposalInviteAccept from "./pages/ProposalInviteAccept";
import EnhancedCreditManagement from "./pages/EnhancedCreditManagement";
import Design from "./pages/Design";
import Info from "./pages/Info";
import InfoManagement from "./pages/InfoManagement";
import OutageManagement from "./pages/OutageManagement";
import Production from "./pages/Production";
import Assembly from "./pages/Assembly";
import DeviceDiagnosis from "./pages/DeviceDiagnosis";
import OrganizationShowcase from "./pages/OrganizationShowcase";
import TestShowcase from "./pages/TestShowcase";
import PreviewHome from "./pages/PreviewHome";

/** Forwards old private /new-site/... links to the matching public address. */
function NewSiteRedirect() {
  const { pathname, search, hash } = useLocation();
  const rest = pathname.replace(/^\/new-site/, "") || "/";
  const mapped = rest === "/about" ? "/about-us" : rest === "/our-solution" ? "/solution" : rest;
  return <Navigate to={`${mapped}${search}${hash}`} replace />;
}
import Overview from "./pages/Overview";
import PreviewAboutUs from "./pages/PreviewAboutUs";
import CaseStudies from "./pages/CaseStudies";
import ResetPassword from "./pages/ResetPassword";
import VerifyPasswordReset from "./pages/VerifyPasswordReset";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { CrmProtectedRoute } from "./components/auth/CrmProtectedRoute";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import RouteSeo from "./components/seo/RouteSeo";
import "./App.css";

function App() {
  const location = useLocation();
  
  useEffect(() => {
    console.log('🎨 [App] Route changed to:', location.pathname);
    console.log('🎨 [App] Search params:', location.search);
    console.log('🎨 [App] Hash:', location.hash);
  }, [location]);
  
  console.log('🎨 [App] Rendering App component');
  
  return (
    <TooltipProvider>
            <RouteSeo />
            <div className="min-h-screen bg-gray-50">
              <Routes>
        <Route path="/" element={<BrandGate isHome><NewSiteHome /></BrandGate>} />
        <Route path="/about-us" element={<BrandGate><NewSiteAbout /></BrandGate>} />
        <Route path="/solution" element={<BrandGate><NewSiteSolution /></BrandGate>} />
        <Route path="/sectors" element={<BrandGate><NewSiteSectors /></BrandGate>} />
        <Route path="/sectors/:sector" element={<BrandGate><NewSiteSectors /></BrandGate>} />
        <Route path="/news" element={<BrandGate><NewSiteNews /></BrandGate>} />
        <Route path="/news/:id" element={<BrandGate><NewSiteNewsArticlePage /></BrandGate>} />
        <Route path="/case-studies" element={<BrandGate><NewSiteCaseStudies /></BrandGate>} />
        <Route path="/case-studies/:slug" element={<BrandGate><NewSiteCaseStudyPage /></BrandGate>} />
        {/* Old live-site and private-preview addresses forward to the new site. */}
        <Route path="/about" element={<Navigate to="/about-us" replace />} />
        <Route path="/our-solution" element={<Navigate to="/solution" replace />} />
        <Route path="/overview" element={<Navigate to="/solution" replace />} />
        <Route path="/new-site/*" element={<NewSiteRedirect />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-reset" element={<VerifyPasswordReset />} />
        <Route path="/login" element={<Login />} />
        <Route path="/test-showcase/:organizationId" element={<TestShowcase />} />
        <Route path="/showcase/:organizationId" element={<OrganizationShowcase />} />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <ErrorBoundary componentName="Dashboard">
                      <Dashboard />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/map" element={<ProtectedRoute><Map /></ProtectedRoute>} />
                <Route path="/dashboard/device-details" element={<ProtectedRoute><DeviceDetails /></ProtectedRoute>} />
                <Route path="/dashboard/device-assignments" element={<ProtectedRoute><DeviceAssignments /></ProtectedRoute>} />
                <Route path="/dashboard/device-groups" element={<ProtectedRoute><DeviceGroups /></ProtectedRoute>} />
                <Route path="/dashboard/gallery" element={<ProtectedRoute><Gallery /></ProtectedRoute>} />
                <Route path="/dashboard/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/dashboard/user-roles" element={<ProtectedRoute><SuperuserGate><UserRoles /></SuperuserGate></ProtectedRoute>} />
                <Route path="/dashboard/admin/customer-success" element={<ProtectedRoute><SuperuserGate><CustomerSuccess /></SuperuserGate></ProtectedRoute>} />
                <Route path="/dashboard/bulk-update" element={<ProtectedRoute><SuperuserGate><BulkUpdate /></SuperuserGate></ProtectedRoute>} />
                <Route path="/dashboard/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                <Route path="/dashboard/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                <Route path="/dashboard/admin/orders" element={<ProtectedRoute><AdminOrders /></ProtectedRoute>} />
                <Route path="/dashboard/sub-customers" element={<ProtectedRoute><OrgFeatureGate feature="customer_admin" adminOnly><SubCustomers /></OrgFeatureGate></ProtectedRoute>} />
                <Route path="/dashboard/sub-customers/:id" element={<ProtectedRoute><OrgFeatureGate feature="customer_admin" adminOnly><SubCustomerDetail /></OrgFeatureGate></ProtectedRoute>} />
                <Route path="/dashboard/admin/white-label" element={<ProtectedRoute><WhiteLabelStudio /></ProtectedRoute>} />
                <Route path="/dashboard/organisation-profile" element={<ProtectedRoute><OrganisationProfile /></ProtectedRoute>} />
                <Route path="/dashboard/api-configuration" element={<ProtectedRoute><OrgFeatureGate feature="api_configuration"><ApiConfiguration /></OrgFeatureGate></ProtectedRoute>} />
                <Route path="/dashboard/credit-management" element={<ProtectedRoute><EnhancedCreditManagement /></ProtectedRoute>} />
                <Route path="/dashboard/design" element={<ProtectedRoute><Design /></ProtectedRoute>} />
                <Route path="/dashboard/info/manage" element={<ProtectedRoute><InfoManagement /></ProtectedRoute>} />
                <Route path="/dashboard/outages/manage" element={<ProtectedRoute><OutageManagement /></ProtectedRoute>} />
                <Route path="/dashboard/production" element={<ProtectedRoute><Production /></ProtectedRoute>} />
                <Route path="/dashboard/assembly" element={<ProtectedRoute><Assembly /></ProtectedRoute>} />
                <Route path="/dashboard/device-diagnosis" element={<ProtectedRoute><OrgFeatureGate feature="device_diagnosis"><DeviceDiagnosis /></OrgFeatureGate></ProtectedRoute>} />
                <Route path="/info" element={<ProtectedRoute><Info /></ProtectedRoute>} />
                <Route path="/dashboard/crm/contacts" element={<CrmProtectedRoute><CrmContacts /></CrmProtectedRoute>} />
                <Route path="/dashboard/crm/staff" element={<CrmProtectedRoute><CrmStaff /></CrmProtectedRoute>} />
                <Route path="/dashboard/crm/partners" element={<CrmProtectedRoute><CrmPartners /></CrmProtectedRoute>} />
                <Route path="/dashboard/crm/companies" element={<CrmProtectedRoute><CrmCompanies /></CrmProtectedRoute>} />
                <Route path="/dashboard/crm/deals" element={<CrmProtectedRoute><CrmDeals /></CrmProtectedRoute>} />
                <Route path="/dashboard/crm/orders" element={<CrmProtectedRoute><CrmOrders /></CrmProtectedRoute>} />
                <Route path="/dashboard/crm/activities" element={<CrmProtectedRoute><CrmActivities /></CrmProtectedRoute>} />
                <Route path="/dashboard/crm/kpi" element={<CrmProtectedRoute><CrmKpi /></CrmProtectedRoute>} />
                <Route path="/dashboard/crm/pipeline" element={<CrmProtectedRoute><CrmPipeline /></CrmProtectedRoute>} />
                <Route path="/dashboard/crm/marketing-calendar" element={<CrmProtectedRoute><MarketingCalendar /></CrmProtectedRoute>} />
                
                <Route path="/dashboard/partner-portal" element={<ProtectedRoute><ErrorBoundary componentName="PartnerPortal"><PartnerPortal /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/dashboard/partner-orders" element={<ProtectedRoute><PartnerOrders /></ProtectedRoute>} />
                <Route path="/dashboard/partner-resources" element={<ProtectedRoute><PartnerResources /></ProtectedRoute>} />
                <Route path="/partner-invite" element={<PartnerInviteAccept />} />
                <Route path="/documentation" element={<Documentation />} />
                <Route path="/features" element={<Navigate to="/solution" replace />} />
                <Route path="/pricing" element={<Navigate to="/solution" replace />} />
                <Route path="/applications" element={<Applications />} />
                <Route path="/equipment" element={<Equipment />} />
                <Route path="/map" element={<Map />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/data-protection" element={<DataProtection />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/environmental" element={<Environmental />} />
                <Route path="/modern-slavery" element={<ModernSlavery />} />
                <Route path="/bribery-policy" element={<BriberyPolicy />} />
                <Route path="/invite" element={<Invite />} />
                <Route path="/invite/:token" element={<Invite />} />
                <Route path="/invite-contact" element={<InviteContactLink />} />
                <Route path="/onboarding" element={<OnboardingSignup />} />
                <Route path="/signup" element={<SignupRedirect />} />
                <Route path="/alerts/unsubscribed" element={<AlertUnsubscribed />} />
            <Route path="/proposal-signup/:token" element={<ProposalSignup />} />
            <Route path="/proposal-invite" element={<ProposalInviteAccept />} />
                <Route path="/proposal/:token" element={<CustomerProposal />} />
              </Routes>
            </div>
            <Toaster />
            <Sonner />
    </TooltipProvider>
  );
}

export default App;
